import { useMemo, useState } from 'react';
import { FolioSection, SectionMast } from '../home/HomeEditorial';
import { formatRegistrarNumber } from './RegistrarNumber';

export function ValueBar({ value }) {
  const clipped = Math.abs(value) > 120;
  const width = (Math.min(Math.abs(value), 120) / 120) * 50;
  return (
    <span className={`rg-value-bar ${value < 0 ? 'is-negative' : 'is-positive'}`} aria-hidden="true">
      <i style={{ width: `${width}%` }} />
      {clipped && <b>^</b>}
    </span>
  );
}

export function KeeperChip({ keeper }) {
  return keeper ? <span className="rg-keeper-chip" title="Keeper">K</span> : null;
}

export default function RegistrarBoard({ picks, teams }) {
  const [teamFilter, setTeamFilter] = useState('all');
  const [roundFilter, setRoundFilter] = useState('all');
  const rounds = useMemo(() => [...new Set(picks.map((pick) => pick.round))].sort((a, b) => a - b), [picks]);
  const visible = useMemo(() => picks.filter((pick) => (
    (teamFilter === 'all' || pick.fantasyTeam === teamFilter)
    && (roundFilter === 'all' || pick.round === roundFilter)
  )), [picks, roundFilter, teamFilter]);
  const draftedPickCount = picks.filter((pick) => !pick.keeper).length;
  const keeperCount = picks.length - draftedPickCount;
  const visibleDraftedCount = visible.filter((pick) => !pick.keeper).length;
  const visibleKeeperCount = visible.length - visibleDraftedCount;

  return (
    <FolioSection numeral="04" className="rg-board border-b border-border bg-bg-surface">
      <SectionMast
        tag="The full board"
        title="Every Board Entry."
        dek={`${draftedPickCount} drafted picks. ${keeperCount} keepers priced separately. Filters change the view, not the record.`}
      />

      <div className="rg-board__filters" aria-label="Draft board filters">
        <div className="rg-filter-row">
          <span>Team</span>
          <button type="button" className={teamFilter === 'all' ? 'is-active' : ''} aria-pressed={teamFilter === 'all'} onClick={() => setTeamFilter('all')}>All</button>
          {teams.map((team) => (
            <button
              type="button"
              key={team.slug}
              className={teamFilter === team.team ? 'is-active' : ''}
              aria-pressed={teamFilter === team.team}
              onClick={() => setTeamFilter(team.team)}
            >
              {team.manager}
            </button>
          ))}
        </div>
        <div className="rg-filter-row">
          <span>Round</span>
          <button type="button" className={roundFilter === 'all' ? 'is-active' : ''} aria-pressed={roundFilter === 'all'} onClick={() => setRoundFilter('all')}>All</button>
          {rounds.map((round) => (
            <button
              type="button"
              key={round}
              className={roundFilter === round ? 'is-active' : ''}
              aria-pressed={roundFilter === round}
              onClick={() => setRoundFilter(round)}
            >
              {round}
            </button>
          ))}
        </div>
      </div>

      <p className="rg-board__count">Showing {visibleDraftedCount} drafted · {visibleKeeperCount} priced keepers · {visible.length} board entries</p>

      <div className="rg-board__table-wrap">
        <table className="rg-board__table">
          <thead>
            <tr>
              <th>OVR</th>
              <th>RD</th>
              <th>PLAYER</th>
              <th>POS</th>
              <th>TEAM</th>
              <th>MANAGER</th>
              <th>VORP</th>
              <th>VALUE</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((pick) => (
              <tr key={pick.overall} className={pick.keeper ? 'is-keeper' : ''}>
                <td>{pick.overall}</td>
                <td>{pick.round}</td>
                <td className="rg-board__player">
                  <KeeperChip keeper={pick.keeper} />{pick.player}
                  {pick.keeper && <small className="rg-board__keeper-note">Priced, not picked</small>}
                </td>
                <td>{pick.position}</td>
                <td>{pick.nflTeam}</td>
                <td>{pick.manager}</td>
                <td>{formatRegistrarNumber(pick.vorp, { precision: 1 })}</td>
                <td>
                  <span className="rg-board__slot-value">
                    {pick.keeper ? 'Keeper ' : ''}{formatRegistrarNumber(pick.keeper ? pick.keeperSurplus : pick.valueVsSlot, { precision: 1, signed: true })}
                  </span>
                  <ValueBar value={pick.keeper ? pick.keeperSurplus : pick.valueVsSlot} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rg-board__cards">
        {visible.map((pick) => (
          <article key={pick.overall} className={`rg-pick-card ${pick.keeper ? 'is-keeper' : ''}`}>
            <header>
              <span>#{pick.overall} · RD {pick.round}</span>
              <span>{pick.position} · {pick.nflTeam}</span>
            </header>
            <h3><KeeperChip keeper={pick.keeper} />{pick.player}</h3>
            {pick.keeper && <small className="rg-board__keeper-note">Priced, not picked</small>}
            <p>{pick.manager} · {pick.fantasyTeam}</p>
            <footer>
              <span>VORP {formatRegistrarNumber(pick.vorp, { precision: 1 })}</span>
              <span>{formatRegistrarNumber(pick.keeper ? pick.keeperSurplus : pick.valueVsSlot, { precision: 1, signed: true })} {pick.keeper ? 'keeper surplus' : 'vs slot'}</span>
            </footer>
            <ValueBar value={pick.keeper ? pick.keeperSurplus : pick.valueVsSlot} />
          </article>
        ))}
      </div>
    </FolioSection>
  );
}
